import './Breadcrumb.scss';
import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../common/Loader/Loader';
import NavigationIcons from './NavigationIcons';
import GroupIcon from '../icons/GroupIcon';
import SidePanel from '../../../components/common/side-panel/SidePanel';
import Tooltip from '../../common/tooltip/ToolTip';
import MenuNavIcon from './NavMenuIcon';
import { setSelectedAsset, setSelectedGroupByName } from '../AssetGroupsSlice';
import { fetchAssetTimezone } from '../../asset/AssetTimezoneSlice';

/* --- compact only on 1024/800 --- */
const getCharLimit = () => {
  if (typeof window === 'undefined') return Number.MAX_SAFE_INTEGER;
  const w = window.innerWidth;
  if (w <= 800) return 20;
  if (w <= 1024) return 45;
  return Number.MAX_SAFE_INTEGER; // big screens: no change
};

const compactBreadcrumb = (segments: string[], wellName: string, limit: number) => {
  const sep = ' > ';
  const total = segments.join(sep).length + (wellName ? sep.length + wellName.length : 0);

  if (total <= limit) return segments;

  // shorten all non-last segments to "a.."
  return segments.map((s, i) => (i === segments.length - 1 ? s : s ? s[0] + '..' : s));
};
/* --- end --- */

const Breadcrumb: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isWellGroupOpen, setIsWellGroupOpen] = React.useState(false);

  const selectedGroup = useAppSelector((state) => state.assetGroups?.selectedGroup);
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);
  const assetGroups = useAppSelector((state) => state.assetGroups?.groups || []);
  const rodliftwellstatusData = useAppSelector((state) => state.rodliftwellstatus.data);
  const selectedAssetGuid = useAppSelector((state) => state.assetGroups.selectedAssetGuid) || '';

  const runStatus = rodliftwellstatusData?.imageOverlayItems;
  const runStatusObj = Array.isArray(runStatus) ? runStatus.find((item) => item.overlayField === 6) : undefined;
  const runStatusValue = runStatusObj?.value ?? '';

  const alarmText =
    (rodliftwellstatusData?.alarms?.length ?? 0) > 0 ? (rodliftwellstatusData?.alarms?.[0]?.value ?? '') : '';

  const asset = selectedGroup?.assets.find((a) => a.assetName === selectedAssetId) ??
    selectedGroup?.assets[0] ?? { industryApplicationId: 0 };

  const groupList = selectedAssetId
    ? findAllGroupsWithAsset(assetGroups, selectedAssetId)
    : selectedGroup
      ? [selectedGroup]
      : [];

  const { assetGroupName, assetId } = useParams();

  useEffect(() => {
    if (assetGroupName && assetGroups && assetGroups.length > 0) {
      const decodedGroupName = decodeURIComponent(assetGroupName);
      dispatch(setSelectedGroupByName({ groupName: decodedGroupName }));

      if (assetId) {
        dispatch(setSelectedAsset({ assetId: decodeURIComponent(assetId) }));
      }
    }
  }, [assetGroupName, assetId, assetGroups, dispatch]);

  useEffect(() => {
    if (selectedAssetId) {
      dispatch(fetchAssetTimezone(selectedAssetGuid));
    }
  }, [selectedAssetId]);

  function findAllGroupsWithAsset(groups: any[], assetId: string): any[] {
    let result: any[] = [];
    for (const group of groups) {
      if (group.assets?.some((asset: any) => asset.assetName === assetId)) {
        result.push(group);
      }
      if (group.childGroups) {
        result = result.concat(findAllGroupsWithAsset(group.childGroups, assetId));
      }
    }
    return result;
  }

  function findGroupPathById(groups: any[], groupId: string | number): any[] {
    for (const group of groups) {
      if (group.id === groupId) return [group];
      if (group.childGroups) {
        const path = findGroupPathById(group.childGroups, groupId);
        if (path.length) return [group, ...path];
      }
    }
    return [];
  }

  const BreadcrumbGroupList = selectedGroup?.id ? findGroupPathById(assetGroups, selectedGroup.id) : [];

  /* --- compact labels only for small screens --- */
  const charLimit = getCharLimit();
  const groupNames = React.useMemo(() => BreadcrumbGroupList.map((g: any) => g.assetGroupName), [BreadcrumbGroupList]);
  const displayNames = React.useMemo(
    () => compactBreadcrumb(groupNames, selectedAssetId || '', charLimit),
    [groupNames, selectedAssetId, charLimit],
  );
  /* --- end --- */

  const { groups, groupHashMap } = useAppSelector((state) => state.assetGroups);

  const handleGroupNameClick = (groupName: string) => {
    let groupId;
    const flattenGroups = (groups: any[]): any[] => {
      let allGroups: any[] = [];
      groups?.forEach((group) => {
        allGroups.push(group);
        if (group.childGroups && group.childGroups.length > 0) {
          allGroups = allGroups.concat(flattenGroups(group.childGroups));
        }
      });
      return allGroups;
    };
    const allGroups = flattenGroups(groups || []);
    const matchingGroup = allGroups.find((g) => g.assetGroupName === groupName);
    if (matchingGroup) {
      groupId = matchingGroup.id;
      dispatch({
        type: 'assetGroups/updateSelectedAsset',
        payload: {
          groupId,
          assetId: undefined,
          searchString: undefined,
          assetIdGuid: undefined,
        },
      });
      const group = groupHashMap[groupId];
      if (group && !group.showAssets) {
        dispatch({
          type: 'assetGroups/updateAssetsDisplayState',
          payload: {
            groupId,
            assetId: undefined,
            searchString: undefined,
            assetIdGuid: undefined,
          },
        });
      }
    }
    navigate(`/group/customViews/${encodeURIComponent(groupName)}`);
  };

  return selectedGroup ? (
    <div className='breadcrumb'>
      <MenuNavIcon />
      <div className='well-group-hierarchy'>
        {BreadcrumbGroupList.map((group, idx) => {
          const isLast = idx === BreadcrumbGroupList.length - 1;
          const isSecondLast = idx === BreadcrumbGroupList.length - 2;

          const displayName =
            charLimit === Number.MAX_SAFE_INTEGER
              ? BreadcrumbGroupList.length > 3 && !isLast && !isSecondLast
                ? `${group.assetGroupName.slice(0, 5)}…`
                : group.assetGroupName
              : displayNames[idx];

          return (
            <React.Fragment key={group.id || idx}>
              <span>
                <span onClick={() => handleGroupNameClick(group.assetGroupName)}>{displayName}</span>
              </span>
              {!isLast && <span className='breadcrumb-separator'>{'>'}</span>}
            </React.Fragment>
          );
        })}
        {selectedAssetId && (
          <>
            {BreadcrumbGroupList.length > 0 && <span className='breadcrumb-separator'>{'>'}</span>}
            <span className='breadcrumb-selected-well-name highlighted'>{selectedAssetId}</span>
          </>
        )}
      </div>

      <div className='group'>
        <span style={{ marginRight: '8px' }}>
          <NavigationIcons asset={asset} />
        </span>
        <span className='breadcrumb-selected-well-name' style={{ marginRight: '12px' }}>
          {selectedAssetId || undefined}
        </span>
        <span style={{ marginRight: '12px' }}>
          <div
            className={
              `breadcrumb-well-status ` +
              (['shutdown', 'stopped', 'unable to run', 'cannot stop', 'stop malf'].some((keyword) =>
                runStatusValue.toLowerCase().includes(keyword),
              )
                ? 'well-status-off'
                : runStatusValue.toLowerCase().includes('run')
                  ? 'well-status-running'
                  : runStatusValue.toLowerCase().includes('idle')
                    ? 'well-status-idle'
                    : '')
            }
          >
            <span>{runStatusValue}</span>
          </div>
        </span>

        {/*  Alarm badge shows only the first alarm */}
        {alarmText && (
          <span className='alarm-badge' style={{ marginRight: '12px' }}>
            {alarmText}
          </span>
        )}

        <span>
          <span onClick={() => setIsWellGroupOpen(true)} style={{ cursor: 'pointer' }}>
            <Tooltip content='Well Groups' direction='right'>
              <GroupIcon color={'#F7F9F9'} useAssetsIcon={true} />
            </Tooltip>
          </span>
        </span>
      </div>

      <SidePanel open={isWellGroupOpen} onClose={() => setIsWellGroupOpen(false)} header='Well Groups' width={'416px'}>
        <ul className='side-panel-well-group-list'>
          {assetGroups.length === 0 ? (
            <Loader />
          ) : groupList.length > 0 ? (
            groupList.map((group, idx) => (
              <li key={group.id || idx} className='side-panel-well-group-items'>
                <GroupIcon color={'#F7F9F9'} useAssetsIcon={true} />
                <span className='side-panel-well-group-name' onClick={() => handleGroupNameClick(group.assetGroupName)}>
                  {group.assetGroupName}
                </span>
              </li>
            ))
          ) : (
            <li>No group found</li>
          )}
        </ul>
      </SidePanel>
    </div>
  ) : (
    <div className='loader-relative'>
      <Loader />
    </div>
  );
};

export default Breadcrumb;
